import { Box, Paper, Grid2 as Grid } from "@mui/material"
import Stack from "@mui/material/Stack"
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { useMeasure } from "@uidotdev/usehooks"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import CustomToolbar from "../components/CustomToolbar"
import Layout from "../components/Layout"
import { PageTitle } from "../components/PageTitle"
import Paginator from "../components/Paginator"
import { useQsFilters } from "../hooks/filters"
import { Videocam } from "@mui/icons-material"
import { useSnackbarMessage } from "../hooks/snackbar"
import DialogConfirm from "../components/DialogConfirm"
// import FileDigitaleForm from "../components/forms/FileDigitaleForm"
import {
  useFilesDigitaliList,
  // useFileDigitaleCreate,
  useFileDigitaleDelete,
} from "../hooks/filesDigitali"
import { ACFormato, ACSupporto, ACVocabolario } from "../components/forms/autocompletes"
import useUserCanWriteAndDelete from "../hooks/permissions"
import IconButtonDelete from "../components/IconButtonDelete"
import LangLink from "../components/LangLink"
import { useIsMobile } from "../hooks/mobile"
import { useIsTablet } from "../hooks/tablet"
import { useAuth } from "../auth"
import { PAGE_SIZE_OPTIONS, usePageSize } from "../hooks/pageSize"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "nome",
  supporto: params.get("supporto") ?? "",
  formato: params.get("formato") ?? "",
  codec: params.get("codec") ?? "",
})

export default function FilesDigitali({
  fondoId,
  supportoId,
  documentoId,
}: {
  fondoId?: number
  supportoId?: number
  documentoId?: number
}) {
  const pageSize = usePageSize()
  const { filters, setFilters, uiFilters, setFiltersDebounced } = useQsFilters(initFilters)
  const { t } = useTranslation()
  const userCanWriteAndDelete = useUserCanWriteAndDelete()
  
  const { user } = useAuth()
  const isMobile = useIsMobile() || user?.display === "mobile"
  const isTablet = (useIsTablet() || user?.display === "tablet") && !isMobile

  const columns: GridColDef[] = useMemo(() => {
    let cols: GridColDef[] = []
    cols.push({
      field: "filename",
      headerName: t("fileDigitale.filename"),
      flex: 1,
      renderCell: (params) => {
        const toLink = `/files/${params.row.id}`
        return (
          <LangLink to={toLink} style={{ color: "black" }}>
            {params.row.filename}
          </LangLink>
        )
      },
    })
    if (isMobile) {
      return cols
    }

    if (isTablet) {
      cols.push({
        field: "segnatura_supporto",
        headerName: t("general.supporto"),
        flex: 1,
      })
      if (userCanWriteAndDelete) {
        cols.push({
          field: "id",
          headerName: t("general.azioni"),
          flex: 1,
          display: "flex",
          align: "right",
          headerAlign: "right",
          hideSortIcons: true,
          sortable: false,
          renderCell: (params) => (
            <>
              <IconButtonDelete onClick={() => deleteModalActions.open(params.row)} />
            </>
          ),
        })
      }
      return cols
    }

    cols.push(
      {
        field: "segnatura_supporto",
        headerName: t("general.supporto"),
        flex: 1,
      },
      {
        field: "tipo_file_data",
        headerName: t("fileDigitale.tipo_file"),
        sortable: false,
        flex: 1,
        renderCell: (params) => {
          return params.row.tipo_file_data?.termine ?? ""
        },
      },
      {
        field: "posizione_file",
        headerName: t("fileDigitale.posizione_file"),
        flex: 1,
      },
    )
    if (userCanWriteAndDelete) {
      cols.push({
        field: "id",
        headerName: t("general.azioni"),
        flex: 1,
        display: "flex",
        align: "right",
        headerAlign: "right",
        hideSortIcons: true,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButtonDelete onClick={() => deleteModalActions.open(params.row)} />
          </>
        ),
      })
    }
    return cols
  }, [t, userCanWriteAndDelete, isMobile, isTablet])

  const { data, isLoading } = useFilesDigitaliList({
    page: String(filters.page),
    page_size: String(pageSize),
    search: filters.search,
    ordering: filters.ordering,
    fondo: fondoId ? String(fondoId) : "",
    supporto: supportoId ? String(supportoId) : (filters.supporto ?? ""),
    documento: documentoId ? String(documentoId) : "",
    formato: filters.formato ?? "",
    codec: filters.codec ?? "",
  })

  // const { mutateAsync: create } = useFileDigitaleCreate()
  const { mutateAsync: remove } = useFileDigitaleDelete()

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  const [ref, { height }] = useMeasure()
  const [toolbarRef, { height: toolbarHeight }] = useMeasure()
  const tableHeight = useMemo(() => {
    if (!height || !toolbarHeight) return
    return height - toolbarHeight
  }, [height, toolbarHeight])

  // const [createModalState, createModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  const filtersCount = useMemo(() => {
    let count = 0
    if (filters.supporto) count++
    if (filters.formato && filters.formato !== "") count++
    if (filters.codec && filters.codec !== "") count++

    return count
  }, [filters.supporto, filters.formato, filters.codec])

  return (
    <Layout padding>
      <Stack sx={{ height: "100%" }}>
        {!fondoId && !supportoId && !documentoId && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <PageTitle
              title={t("general.file_digitali")}
              breadcrumbs={[
                {
                  label: t("general.file_digitali"),
                  iconType: Videocam,
                },
              ]}
            />

            {/* <Button
              variant="contained"
              color="primary"
              onClick={() => {
                createModalActions.open()
              }}
            >
              {t("formato.cta_aggiungi")}
            </Button> */}
          </Box>
        )}
        <Box flexGrow={1}>
          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomToolbar
              count={data?.count}
              search={uiFilters.search}
              setSearch={(search) => setFiltersDebounced({ ...filters, search, page: 1 })}
              clearFilters={() => setFilters({ search: "", page: 1, supporto: "", formato: "", codec: "" })}
              filtersCount={filtersCount}
              ref={toolbarRef}
            >
              <Grid container spacing={2}>
                <Grid size={3}>
                  <ACSupporto
                    label={t("general.supporto")}
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    value={Number(uiFilters.supporto)}
                    onChange={(value) => {
                      setFilters({ ...filters, supporto: value ? String(value) : "", page: 1 })
                    }}
                  />
                </Grid>
                <Grid size={3}>
                  <ACFormato
                    label={t("general.formato")}
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    value={Number(uiFilters.formato)}
                    onChange={(value) => {
                      setFilters({ ...filters, formato: value ? String(value) : "", page: 1 })
                    }}
                  />
                </Grid>
                <Grid size={3}>
                  <ACVocabolario
                    label={t("fileDigitale.codec")}
                    vocabolario="codec"
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    value={Number(uiFilters.codec)}
                    onChange={(value) => {
                      setFilters({ ...filters, codec: value ? String(value) : "", page: 1 })
                    }}
                  />
                </Grid>
              </Grid>
            </CustomToolbar>
            <div
              style={{
                flexGrow: 1,
                overflow: "scroll",
                height: tableHeight ?? undefined,
              }}
              ref={ref}
            >
              {height && (
                <DataGrid
                  disableColumnMenu
                  disableColumnResize
                  // density="compact"
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationModel={{ page: 0, pageSize }}
                  sx={{
                    padding: 2,
                    "&.MuiDataGrid-root": {
                      border: "none",
                    },
                  }}
                  localeText={{
                    noRowsLabel: t("general.nessun_dato_disponibile"),
                  }}
                  loading={isLoading}
                  slots={{
                    footer: () => (
                      <Paginator
                        numPages={numPages}
                        page={filters.page}
                        setPage={(page) =>
                          setFilters({
                            ...filters,
                            page,
                          })
                        }
                      />
                    ),
                    toolbar: null,
                  }}
                  rows={data?.results ?? ([] as GridRowsProp)}
                  columns={columns}
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const sortSign = model[0].sort === "asc" ? "" : "-"
                      const newOrdering = `${sortSign}${model[0].field}`
                      setFilters({ ordering: newOrdering, page: 1 })
                    }
                  }}
                />
              )}
            </div>
          </Paper>
        </Box>
      </Stack>

      {/* <DialogForm<FileDigitale>
        open={createModalState.isOpen}
        handleClose={createModalActions.close}
        title={t("formato.cta_aggiungi")}
        FormComponent={FileDigitaleForm}
        save={(values: FileDigitale) => {
          return create(values).then(() => {
            createModalActions.close()
          })
        }}
      /> */}

      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("formato.cta_elimina")}
        subtitle={
          <span>
            {t("formato.cta_subtitle_elimina")} <b>{deleteModalState.value?.nome}</b> ?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("formato.esito_elimina_ok"))
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
    </Layout>
  )
}
